import React, { useEffect, useState } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  IconButton,
  Button,
} from 'src/components/ui';
import { CloseIcon, CheckIcon } from '@chakra-ui/icons';
import { RIGHT_SIDEBAR_WIDTH_COLLAPSED } from 'src/components/campaigns/utils/constants';

interface CampaignStepsOverlayProps {
  isVisible: boolean;
  onDismiss: () => void;
}

const STEPS = [
  {
    number: 1,
    title: 'Campaign Details',
    description: 'Set your info & channels',
  },
  {
    number: 2,
    title: 'Generate Designs',
    description: 'AI creates your ad visuals',
  },
  {
    number: 3,
    title: 'Review & Publish',
    description: 'Set date, budget & launch',
  },
];

/**
 * CampaignStepsOverlay Component
 *
 * A lateral toast/snackbar that slides in from the right.
 * Shows the 3 campaign creation steps with a vertical timeline.
 * Less intrusive than a full modal.
 */
const CampaignStepsOverlay: React.FC<CampaignStepsOverlayProps> = ({
  isVisible,
  onDismiss,
}) => {
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      // Start animation after a brief delay
      setTimeout(() => setIsAnimating(true), 50);

      // Auto-dismiss after 8 seconds
      const autoDismiss = setTimeout(() => {
        handleClose();
      }, 8000);

      return () => clearTimeout(autoDismiss);
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => setShouldRender(false), 400);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setShouldRender(false);
      onDismiss();
    }, 400);
  };

  if (!shouldRender) return null;

  return (
    <>
      {/* Subtle backdrop - only slight darkening, no blur */}
      {/* Stops before right sidebar to avoid double overlay */}
      <Box
        position="fixed"
        top={0}
        left={0}
        right={RIGHT_SIDEBAR_WIDTH_COLLAPSED}
        bottom={0}
        bg="blackAlpha.200"
        zIndex={10199}
        opacity={isAnimating ? 1 : 0}
        transition="opacity 0.3s ease-out"
        onClick={handleClose}
      />

      {/* Dark overlay for Right Sidebar - needs higher z-index than RightSidebar (10200) */}
      <Box
        position="fixed"
        top={0}
        right={0}
        bottom={0}
        w={RIGHT_SIDEBAR_WIDTH_COLLAPSED}
        zIndex={10202}
        bg="blackAlpha.200"
        opacity={isAnimating ? 1 : 0}
        transition="opacity 0.3s ease-out"
        pointerEvents="none"
      />

      {/* Toast container - slides in from right */}
      <Box
        position="fixed"
        top="50%"
        right={isAnimating ? '24px' : '-400px'}
        transform="translateY(-50%)"
        transition="right 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
        zIndex={10200}
        w="320px"
        maxW="calc(100vw - 48px)"
      >
        <Box
          bg="white"
          borderRadius="20px"
          boxShadow="0 20px 50px -10px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)"
          overflow="hidden"
          position="relative"
        >
          {/* Header with gradient accent */}
          <Box
            bg="linear-gradient(135deg, #F6851E 0%, #FF9F4A 100%)"
            px={5}
            py={4}
            position="relative"
          >
            {/* Decorative circle */}
            <Box
              position="absolute"
              top="-20px"
              right="-20px"
              w="60px"
              h="60px"
              borderRadius="full"
              bg="rgba(255,255,255,0.1)"
            />

            <HStack justify="space-between" align="center">
              <Text fontSize="md" fontWeight="bold" color="white">
                Quick Guide
              </Text>
              <IconButton
                aria-label="Close"
                icon={<CloseIcon boxSize={2} />}
                size="xs"
                variant="ghost"
                color="white"
                _hover={{ bg: 'rgba(255,255,255,0.2)' }}
                onClick={handleClose}
              />
            </HStack>
          </Box>

          {/* Steps with vertical timeline */}
          <VStack spacing={0} align="stretch" py={4} px={5}>
            {STEPS.map((step, index) => (
              <HStack
                key={step.number}
                spacing={4}
                position="relative"
                pb={index < STEPS.length - 1 ? 4 : 0}
                sx={{
                  animation: `stepFadeIn 0.3s ease-out ${0.1 + index * 0.1}s both`,
                  '@keyframes stepFadeIn': {
                    '0%': {
                      opacity: 0,
                      transform: 'translateX(10px)',
                    },
                    '100%': {
                      opacity: 1,
                      transform: 'translateX(0)',
                    },
                  },
                }}
              >
                {/* Timeline line */}
                {index < STEPS.length - 1 && (
                  <Box
                    position="absolute"
                    left="15px"
                    top="32px"
                    bottom="0"
                    w="2px"
                    bg="orange.200"
                    borderRadius="full"
                  />
                )}

                {/* Step number circle */}
                <Box
                  w="32px"
                  h="32px"
                  bg="orange.500"
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                  boxShadow="0 2px 8px rgba(246, 133, 30, 0.3)"
                  position="relative"
                  zIndex={1}
                >
                  <Text color="white" fontWeight="bold" fontSize="sm">
                    {step.number}
                  </Text>
                </Box>

                {/* Step content */}
                <VStack align="start" spacing={0} flex={1}>
                  <Text fontWeight="600" color="gray.800" fontSize="sm">
                    {step.title}
                  </Text>
                  <Text color="gray.500" fontSize="xs">
                    {step.description}
                  </Text>
                </VStack>
              </HStack>
            ))}
          </VStack>

          {/* Footer with action button */}
          <Box px={5} pb={4}>
            <Button
              size="sm"
              w="full"
              colorScheme="orange"
              borderRadius="10px"
              fontWeight="600"
              onClick={handleClose}
              _hover={{
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(246, 133, 30, 0.3)',
              }}
              _active={{ transform: 'translateY(0)' }}
              transition="all 0.2s"
              leftIcon={<CheckIcon boxSize={3} />}
            >
              Got it!
            </Button>
          </Box>

          {/* Progress bar for auto-dismiss */}
          <Box
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            h="3px"
            bg="gray.100"
            overflow="hidden"
          >
            <Box
              h="full"
              bg="orange.400"
              sx={{
                animation: isAnimating ? 'progress 8s linear forwards' : 'none',
                '@keyframes progress': {
                  '0%': { width: '100%' },
                  '100%': { width: '0%' },
                },
              }}
            />
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default CampaignStepsOverlay;
